import { useLayoutEffect, useRef, useState } from 'react';
import { AudioLines, Mic, Plus, Square } from 'lucide-react';
import { composerDir } from '../../../lib/composer-direction';
import { isImeComposing } from '../../../lib/ime-composition';
import type { Dictation } from '../../../lib/use-dictation';
import { Button, IconButton } from '../ui';
import { ICON_BTN } from './styles';
import { ContactMenuIcon, FileMenuIcon, LocationMenuIcon, SendIcon, TimeMenuIcon, TranscribingSpinner } from './icons';
import { AttachMenu, type AttachMenuItem } from './AttachMenu';
import { RecordingBar } from './RecordingBar';
import styles from './ComposerPill.module.css';

// The one Symphony composer — the Figma "Prompt input": a 100px white box laid out
// as a column (text field on top, control row pinned to the bottom). Used by BOTH
// the global bottom composer (HomeComposer) and the in-chat composer (ChatSurface)
// so they are pixel-identical. Presentation only — the parent owns the value, the
// file input, dictation, and what "send" does.
export interface ComposerPillProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  placeholder: string;
  onAttach: () => void;
  attachLabel: string;
  // When provided, the "+" opens a menu (Choose file / Contact / Location / Share a time) instead
  // of firing the file picker directly. Left unset (e.g. HomeComposer) → the "+" stays a bare
  // file-picker button. Location + time are optional extras layered on top of the file+contact menu.
  onAttachContact?: () => void;
  chooseFileLabel?: string;
  contactLabel?: string;
  onAttachLocation?: () => void;
  locationLabel?: string;
  onAttachTime?: () => void;
  shareTimeLabel?: string;
  dictation: Dictation;
  dictateLabel: string;
  transcribingLabel?: string;
  cancelLabel: string;
  // Start a live voice call. Distinct from dictation (speech → text in the
  // box) and from a voice note (recorded audio sent as a message): this opens
  // a real-time conversation. Unset → no call affordance. The call surface itself
  // owns mute/settings/end (VoiceCallOverlay) — the composer only launches it.
  onStartCall?: () => void;
  // Screen-reader/tooltip text for the call launcher (e.g. "Start a call").
  startCallLabel?: string;
  // The launcher's VISIBLE label — the design's "Speak" pill (Figma 12835:78977).
  speakLabel?: string;
  // Keep the "Speak" pill showing whenever the field is EMPTY, even while it's focused — for the
  // auto-focused mobile FAB (HomeComposer), which otherwise opens focused and only ever shows the
  // disabled Send arrow. The in-chat composer leaves this off, so focusing its empty field still
  // yields to Send (its resting-vs-typing swap).
  speakWhenEmpty?: boolean;
  canSend: boolean;
  sendLabel: string;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onInputActiveChange?: (active: boolean) => void;
  showStop?: boolean;
  onStop?: () => void;
  stopLabel?: string;
  autoFocus?: boolean;
  // Optional reply-preview node rendered inside the pill, above the textarea.
  replyPreview?: React.ReactNode;
  // Scale the dictation waveform up for the full-size chat (Figma "Voice Recording" L) — the compact
  // panel keeps the small design bars. Off by default.
  recordingLarge?: boolean;
}

export function ComposerPill({
  value,
  onChange,
  onSubmit,
  placeholder,
  onAttach,
  attachLabel,
  onAttachContact,
  chooseFileLabel,
  contactLabel,
  onAttachLocation,
  locationLabel,
  onAttachTime,
  shareTimeLabel,
  dictation,
  dictateLabel,
  transcribingLabel,
  cancelLabel,
  onStartCall,
  startCallLabel,
  speakLabel,
  speakWhenEmpty,
  canSend,
  sendLabel,
  onPaste,
  onInputActiveChange,
  showStop,
  onStop,
  stopLabel,
  autoFocus,
  replyPreview,
  recordingLarge,
}: ComposerPillProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Focus + caret-to-end on mount. A layout effect keeps it in the same frame as
  // the mount so mobile Safari opens the keyboard.
  useLayoutEffect(() => {
    if (!autoFocus) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [autoFocus]);
  // Focus drives the Speak↔Send swap (Figma "Keyboard Open" vs "Speak Default"): the Speak pill is
  // the RESTING affordance of an empty composer, but the moment the field is focused it yields to the
  // (disabled) Send arrow, exactly as typing does.
  const [focused, setFocused] = useState(false);
  const recording = dictation.status === 'recording';
  const transcribing = dictation.status === 'transcribing';
  const effectivePlaceholder = transcribing ? transcribingLabel ?? placeholder : placeholder;

  // Recording keeps the horizontal bar (waveform + timer); otherwise the column
  // layout — field on top, control row pinned to the bottom.
  const containerStyle: React.CSSProperties = recording
    ? {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        borderRadius: 20,
        background: 'var(--ds-surface-sunken)',
        padding: 12,
        boxShadow: '0 -4px 18px 0 rgba(var(--ds-black-rgb), 0.1)',
        transition: 'box-shadow 160ms ease',
      }
    : {
        display: 'flex',
        minHeight: 118,
        // Figma composer spec (node 14701:126613, "Prompt box" — 720×118): 12px padding all sides, 20 radius.
        padding: 12,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        borderRadius: 20,
        background: 'var(--ds-surface-sunken)',
        // Flat resting elevation — no accent focus ring (Symphony composer spec).
        boxShadow: '0 -4px 18px 0 rgba(var(--ds-black-rgb), 0.1)',
      };

  return (
    <div style={containerStyle}>
      {recording ? (
        <RecordingBar
          seconds={dictation.seconds}
          levels={dictation.levels}
          cancelLabel={cancelLabel}
          sendLabel={sendLabel}
          onCancel={dictation.cancel}
          onConfirm={dictation.confirm}
          full={recordingLarge}
        />
      ) : (
        <>
          <div style={{ width: '100%' }}>
            {replyPreview}
            <textarea
            ref={textareaRef}
            className="orion-input"
            // Bidi: follow the direction of what's being TYPED (first strong char), falling back to the
            // UI language while empty so the placeholder sits naturally (mirrors native #6397/composer-direction.ts —
            // `dir="auto"` resolves an empty field to plain ltr, scrambling a mixed-language placeholder).
            dir={composerDir(value)}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              onInputActiveChange?.(e.target.value.length > 0);
            }}
            onFocus={() => {
              setFocused(true);
              onInputActiveChange?.(true);
            }}
            onBlur={() => {
              setFocused(false);
              onInputActiveChange?.(value.length > 0);
            }}
            onPaste={onPaste}
            onKeyDown={(e) => {
              // The Enter that confirms an IME (kana/kanji, pinyin, Hangul) candidate is not "send".
              if (isImeComposing(e)) return;
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
            placeholder={effectivePlaceholder}
            rows={1}
            disabled={transcribing}
            suppressHydrationWarning
            style={{
              width: '100%',
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              borderRadius: 0,
              paddingBlock: 2,
              paddingInlineEnd: 2,
              paddingInlineStart: 6,
              resize: 'none',
              // Figma input field: 40px single-line box that grows with content up
              // to 4 rows (4 × 24px line-height + 4px padding = 100px) then scrolls.
              // `field-sizing: content` drives the auto-grow between min and max.
              fieldSizing: 'content',
              minHeight: 40,
              maxHeight: 100,
              overflowY: 'auto',
              // 16px matches the message body (and clears iOS's <16px zoom-on-focus).
              fontSize: 16,
              fontWeight: 400,
              // Figma prompt-box placeholder (14701:126614): 16px / 24 line-height / -0.32px tracking
              // (−2% of 16px). Applies to the placeholder and the typed text alike (one field).
              letterSpacing: '-0.32px',
              lineHeight: '24px',
              // NEVER enable discretionary ligatures on a text field: `dlig` ligates ま+す into the
              // masu mark 〼 (U+303C, the historical ligature of ます) in a CJK fallback font, so a
              // Japanese user's typed ます renders as 〼. A composer must render typed characters
              // literally in every script — disable ligatures outright (font-feature-settings
              // inherits, so this also blocks any ancestor turning `dlig` back on).
              fontVariantLigatures: 'none',
              // Typed text = primary ink; the placeholder is secondary (CSS below).
              color: 'var(--sym-ink)',
            }}
          />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 8, marginTop: 12 }}>
            {onAttachContact ? (
              <AttachMenu
                triggerLabel={attachLabel}
                triggerClassName={styles.chatRound}
                items={[
                  { key: 'file', label: chooseFileLabel ?? attachLabel, icon: <FileMenuIcon />, onSelect: onAttach },
                  ...(onAttachContact
                    ? [{ key: 'contact', label: contactLabel ?? attachLabel, icon: <ContactMenuIcon />, onSelect: onAttachContact } as AttachMenuItem]
                    : []),
                  ...(onAttachLocation
                    ? [{ key: 'location', label: locationLabel ?? attachLabel, icon: <LocationMenuIcon />, onSelect: onAttachLocation } as AttachMenuItem]
                    : []),
                  ...(onAttachTime
                    ? [{ key: 'time', label: shareTimeLabel ?? attachLabel, icon: <TimeMenuIcon />, onSelect: onAttachTime } as AttachMenuItem]
                    : []),
                ]}
              />
            ) : (
              <button type="button" onClick={onAttach} aria-label={attachLabel} title={attachLabel} style={{ ...ICON_BTN, borderRadius: 'var(--ds-radius-full)' }}>
                <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              </button>
            )}
            <div style={{ flex: 1 }} />
            {dictation.supported && !transcribing && (
              <IconButton size="small" onClick={dictation.start} aria-label={dictateLabel} title={dictateLabel} className={styles.chatRound}>
                <Mic size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              </IconButton>
            )}
            {transcribing && (
              <span aria-hidden className="orion-mic-live" style={{ ...ICON_BTN, color: 'var(--accent-solid)' }}>
                <TranscribingSpinner />
              </span>
            )}
            {showStop ? (
              <IconButton
                variant="contained"
                color="standard"
                size="small"
                onClick={onStop}
                aria-label={stopLabel ?? ''}
                className={styles.chatRound}
              >
                <Square size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              </IconButton>
            ) : canSend || (focused && !speakWhenEmpty) || !onStartCall || !speakLabel ? (
              // Send shows once there's text OR the field is focused ("Keyboard Open" — disabled until
              // something is typed). Black round Send, per Figma 12835:79044 / "Typing". `speakWhenEmpty`
              // (the mobile FAB) keeps Speak while the empty field is focused; typing still swaps to Send.
              <IconButton
                variant="contained"
                color="standard"
                size="small"
                type="submit"
                disabled={!canSend}
                aria-label={sendLabel}
                className={`${styles.swapIn} ${styles.chatRound} ${styles.sendButton}`}
              >
                <SendIcon />
              </IconButton>
            ) : (
              // Empty AND blurred ("Speak Default") → the black "Speak" pill that opens the live call
              // surface. Focusing or typing swaps Send back in (the swap fades/scales in).
              <Button
                size="small"
                onClick={onStartCall}
                title={startCallLabel}
                className={`${styles.swapIn} ${styles.chatRound}`}
                startIcon={<AudioLines size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}
              >
                {speakLabel}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
